import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Car, Edit2, Plus, Trash2 } from 'lucide-react';
import { useI18n } from '@/shared/i18n';
import { useServices, useToast, getErrorMessage, useDeleteConfirmation, useSelectedUnit } from '@/app/providers';
import { Button } from '@/core/ui/components/Button';
import { Input } from '@/core/ui/components/Input';
import type { Vehicle } from '@yawi/domain';

interface VehicleSectionProps {
  userId: string;
  userName?: string;
}

const VEHICLE_TYPES = [
  { value: 'CAR', label: 'Auto' },
  { value: 'MOTORCYCLE', label: 'Motocicleta' },
  { value: 'TRUCK', label: 'Camioneta' },
  { value: 'SUV', label: 'SUV' },
  { value: 'VAN', label: 'Van' },
  { value: 'OTHER', label: 'Otro' },
] as const;

type VehicleType = typeof VEHICLE_TYPES[number]['value'];

interface NewVehicleForm {
  plateNumber: string;
  brand: string;
  model: string;
  color: string;
  type: VehicleType;
}

const initialForm: NewVehicleForm = {
  plateNumber: '',
  brand: '',
  model: '',
  color: '',
  type: 'CAR',
};

export const VehicleSection: React.FC<VehicleSectionProps> = ({ userId }) => {
  const { t } = useI18n();
  const toast = useToast();
  const { confirmDelete } = useDeleteConfirmation();
  const { vehicleRepo, currentUser } = useServices();
  const { selectedUnitId, hasMultipleUnits } = useSelectedUnit();
  const isPrincipalOfActiveUnit = hasMultipleUnits
    ? (currentUser?.units?.find((assignment) => assignment.unitId === selectedUnitId)?.isPrincipal ?? false)
    : (currentUser?.isPrincipal ?? false);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVehicleId, setEditingVehicleId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [newVehicle, setNewVehicle] = useState<NewVehicleForm>(initialForm);

  // Con más de una unidad, la lista depende de la casa activa en el sidebar
  // — el backend regresa combinados los de toda la casa (varios residentes
  // comparten visibilidad a propósito), pero cada vehículo sí sabe a qué
  // unidad pertenece.
  const visibleVehicles = useMemo(() => {
    if (!hasMultipleUnits) return vehicles;
    return vehicles.filter((v) => v.unitId === selectedUnitId);
  }, [vehicles, hasMultipleUnits, selectedUnitId]);

  const loadVehicles = useCallback(async () => {
    try {
      const myVehicles = await vehicleRepo.getMyVehicles();
      setVehicles(myVehicles);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      toast.error('Error al cargar vehículos');
    } finally {
      setInitialLoading(false);
    }
  }, [vehicleRepo, toast]);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  const openAddModal = () => {
    setEditingVehicleId(null);
    setNewVehicle(initialForm);
    setIsModalOpen(true);
  };

  const openEditModal = (v: Vehicle) => {
    setEditingVehicleId(v.id);
    setNewVehicle({
      plateNumber: v.plateNumber,
      brand: v.brand,
      model: v.model,
      color: v.color || '',
      type: v.type,
    });
    setIsModalOpen(true);
  };

  const handleSaveVehicle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newVehicle.plateNumber || !newVehicle.brand || !newVehicle.model) return;
    setLoading(true);

    try {
      if (editingVehicleId) {
        const current = vehicles.find(v => v.id === editingVehicleId);
        if (!current) return;
        const updatedVehicle = await vehicleRepo.updateVehicle({
          ...current,
          plateNumber: newVehicle.plateNumber.toUpperCase().replace(/\s/g, ''),
          brand: newVehicle.brand,
          model: newVehicle.model,
          color: newVehicle.color || undefined,
          type: newVehicle.type,
        });
        setVehicles(prev => prev.map(v => v.id === updatedVehicle.id ? updatedVehicle : v));
        toast.success('Vehículo actualizado correctamente.');
      } else {
        const createdVehicle = await vehicleRepo.createVehicle({
          plateNumber: newVehicle.plateNumber.toUpperCase().replace(/\s/g, ''),
          brand: newVehicle.brand,
          model: newVehicle.model,
          color: newVehicle.color || undefined,
          type: newVehicle.type,
          unitId: hasMultipleUnits && selectedUnitId ? selectedUnitId : undefined,
        });
        setVehicles(prev => [...prev, createdVehicle]);
        toast.success('Vehículo registrado correctamente.');
      }
      setIsModalOpen(false);
      setEditingVehicleId(null);
      setNewVehicle(initialForm);
    } catch (error) {
      toast.error(getErrorMessage(error), editingVehicleId ? 'Error al actualizar vehículo' : 'Error al registrar vehículo');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVehicle = async (id: string, plateNumber: string) => {
    await confirmDelete({
      itemName: plateNumber,
      itemType: 'Vehículo',
      onConfirm: async () => {
        try {
          await vehicleRepo.deleteVehicle(id);
          setVehicles(prev => prev.filter(v => v.id !== id));
          toast.success('Vehículo eliminado');
        } catch (error) {
          toast.error(getErrorMessage(error), 'Error al eliminar vehículo');
        }
      }
    });
  };

  const getVehicleTypeLabel = (type: string) => {
    return VEHICLE_TYPES.find(t => t.value === type)?.label || type;
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in">
      <div className="flex justify-between items-center">
        <p className="text-slate-500 text-sm">
          {isPrincipalOfActiveUnit
            ? 'Vehículos registrados en esta unidad.'
            : 'Vehículos registrados en esta unidad. Solo el residente principal puede administrarlos.'}
        </p>
        {isPrincipalOfActiveUnit && (
          <Button size="sm" icon={<Plus size={16} />} onClick={openAddModal}>Registrar Vehículo</Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {visibleVehicles.map(v => (
          <div key={v.id} className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-col items-center text-center group hover:border-primary-200 transition-colors relative">
            {isPrincipalOfActiveUnit && (
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-colors">
                <button onClick={() => openEditModal(v)} className="p-1.5 text-slate-300 hover:text-primary-600 transition-colors">
                  <Edit2 size={14} />
                </button>
                <button onClick={() => handleDeleteVehicle(v.id, v.plateNumber)} className="p-1.5 text-slate-300 hover:text-red-500 transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            )}
            <div className="p-3 bg-white rounded-full shadow-sm mb-3 text-slate-600 group-hover:text-primary-600">
              <Car size={24} />
            </div>
            <h4 className="text-2xl font-mono font-bold text-slate-800 tracking-widest mb-1">{v.plateNumber}</h4>
            <p className="text-sm text-slate-500 mb-1">{v.brand} {v.model}</p>
            {v.color && <p className="text-xs text-slate-400 mb-2">{v.color}</p>}
            <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${
              v.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700' :
              v.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
              'bg-slate-100 text-slate-600'
            }`}>
              {v.status === 'ACTIVE' ? 'Activo' : v.status === 'PENDING' ? 'Pendiente' : 'Inactivo'}
            </span>
            {v.ownerName && (
              <p className="text-[11px] text-slate-400 mt-2 border-t border-slate-100 pt-2 w-full">
                Registrado por <span className="font-medium text-slate-500">{v.ownerName}</span>
              </p>
            )}
          </div>
        ))}
        {visibleVehicles.length === 0 && (
          <div className="col-span-3 text-center p-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
            <Car size={32} className="mx-auto text-slate-300 mb-2" />
            <p className="text-slate-400 text-sm italic">No tienes vehículos registrados.</p>
          </div>
        )}
      </div>

      {/* Vehicle Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">{editingVehicleId ? 'Editar Vehículo' : 'Registrar Vehículo'}</h3>
              <button onClick={() => { setIsModalOpen(false); setEditingVehicleId(null); }} className="text-slate-400 text-2xl leading-none">&times;</button>
            </div>
            <form onSubmit={handleSaveVehicle} className="p-6 space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg text-center border border-slate-200 mb-2">
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Número de Placa</label>
                <input
                  className="w-full text-center text-3xl font-mono font-bold bg-transparent border-none focus:ring-0 placeholder-slate-300 uppercase tracking-widest text-slate-800"
                  placeholder="ABC-123"
                  value={newVehicle.plateNumber}
                  onChange={e => setNewVehicle({ ...newVehicle, plateNumber: e.target.value.toUpperCase() })}
                  required
                  maxLength={10}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Marca"
                  placeholder="Ej. Toyota"
                  value={newVehicle.brand}
                  onChange={e => setNewVehicle({ ...newVehicle, brand: e.target.value })}
                  required
                />
                <Input
                  label="Modelo"
                  placeholder="Ej. Corolla"
                  value={newVehicle.model}
                  onChange={e => setNewVehicle({ ...newVehicle, model: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Color"
                  placeholder="Ej. Blanco"
                  value={newVehicle.color}
                  onChange={e => setNewVehicle({ ...newVehicle, color: e.target.value })}
                />
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Tipo</label>
                  <select
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    value={newVehicle.type}
                    onChange={e => setNewVehicle({ ...newVehicle, type: e.target.value as VehicleType })}
                  >
                    {VEHICLE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => { setIsModalOpen(false); setEditingVehicleId(null); }}>Cancelar</Button>
                <Button type="submit" variant="primary" className="flex-1" isLoading={loading}>{editingVehicleId ? 'Guardar' : 'Registrar'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
